import React from 'react';
import { useResetPasswordContext } from "../context/reset_password_context";
import { ForgotPasswordRoute, LoginRoute, ResetPasswordRoute } from "../../../../../core/routes/route_constants";
import { useNavigate } from "react-router-dom";
import {
  Heart,
  Lock,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import '../../forgotpassword/styles/animation.css';

const ResetPasswordComponent = () => {
    const navigate = useNavigate();
    const {
        password,
        passwordErrorMessage,
        confirmPassword,
        confirmPasswordErrorMessage,
        handlePasswordChange,
        handleConfirmPasswordChange,
        handleResetPasswordClick,
        isButtonDisabled,
        name,
        init,
        error,
        isProcessing,
        isSuccess,
    } = useResetPasswordContext();

    if(!init){
        return null;
    }

    if(error){
        return (
            <div className="md:min-h-screen text-white flex items-center justify-center px-4 md:py-8 max-sm:min-h-auto">
              <div className="w-full max-w-md animate-fade-in">
                {/* Error Card with gradient border */}
                <div className="gradient-border p-0.5 shadow-xl shadow-red-950/10">
                  <div className="bg-gradient-to-b from-slate-950/90 to-fuchsia-950/70 backdrop-blur-md rounded-lg overflow-hidden relative">
                    {/* Decorative elements */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-900/0 via-red-700 to-red-900/0"></div>
                    
                    <div className="px-6 py-8 md:px-10 md:py-10">
                      <div className="text-center py-2">
                        <div className="mx-auto w-16 h-16 bg-gradient-to-r from-red-700 via-red-800 to-red-950 rounded-full flex items-center justify-center animate-glow mb-6">
                          <AlertCircle size={26} className="text-white animate-pulse-soft" />
                        </div>
                        <h2 className="text-2xl font-bold mb-1">Invalid or Expired Link</h2>
                        <p className="text-pink-300 mb-6">
                          The password reset link is invalid or has expired.
                        </p>
                        <button
                          onClick={() => navigate(ForgotPasswordRoute)}
                          className="text-base w-full flex justify-center items-center py-3.5 px-4 bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-lg text-white font-medium transition-all duration-300 animate-float-subtle btn-hover-effect relative overflow-hidden group"
                        >
                          <span className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-red-500/0 via-white/10 to-red-500/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
                          <span className="flex items-center">
                            <span>Request New Link</span>
                            <ArrowRight className="ml-2" size={18} />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        );
    }

    return (
        <div className="md:min-h-screen text-white flex items-center justify-center px-4 md:py-8 max-sm:min-h-auto">
          <div className="w-full max-w-md animate-fade-in">
            {/* Reset Password Card with gradient border */}
            <div className="gradient-border p-0.5 shadow-xl shadow-red-950/10">
              <div className="bg-gradient-to-b from-slate-950/90 to-fuchsia-950/70 backdrop-blur-md rounded-lg overflow-hidden relative">
                {/* Decorative elements */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-900/0 via-red-700 to-red-900/0"></div>
                
                <div className="px-6 py-8 md:px-10 md:py-10">
                  {!isSuccess ? (
                    <>
                      <div className="text-center mb-10">
                        <div className="mx-auto w-16 h-16 bg-gradient-to-r from-red-700 via-red-800 to-red-950 rounded-full flex items-center justify-center animate-glow mb-6">
                          <Heart size={26} className="text-white animate-pulse-soft" />
                        </div>
                        <h1 className="text-3xl font-bold mb-1">Reset Your Password</h1>
                        <p className="text-pink-300 mt-2">Hi {name}, please enter your new password.</p>
                      </div>
                      
                      {error && (
                        <div className="mb-6 p-3 bg-red-900/30 border border-red-500/30 rounded-lg text-white text-sm">
                          {error}
                        </div>
                      )}
                      
                      <form onSubmit={(e) => { e.preventDefault(); handleResetPasswordClick(); }} className="space-y-6">
                        <div className="space-y-2">
                          <label htmlFor="password" className="block text-sm font-medium text-pink-200">New Password</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Lock size={18} className="text-pink-400" />
                            </div>
                            <input
                              id="password"
                              name="password"
                              type="password"
                              required
                              value={password}
                              onChange={handlePasswordChange}
                              className="text-base block w-full pl-10 pr-3 py-3 bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white placeholder-pink-300/70 transition-all shadow-inner shadow-black/20 input-focus-effect outline-none"
                              placeholder="Enter New Password"
                            />
                          </div>
                          {passwordErrorMessage && (
                            <div className="text-red-400 text-sm mt-1">
                              {passwordErrorMessage}
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <label htmlFor="confirmPassword" className="block text-sm font-medium text-pink-200">Confirm Password</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Lock size={18} className="text-pink-400" />
                            </div>
                            <input
                              id="confirmPassword"
                              name="confirmPassword"
                              type="password"
                              required
                              value={confirmPassword}
                              onChange={handleConfirmPasswordChange}
                              className="text-base block w-full pl-10 pr-3 py-3 bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white placeholder-pink-300/70 transition-all shadow-inner shadow-black/20 input-focus-effect outline-none"
                              placeholder="Confirm New Password"
                            />
                          </div>
                          {confirmPasswordErrorMessage && (
                            <div className="text-red-400 text-sm mt-1">
                              {confirmPasswordErrorMessage}
                            </div>
                          )}
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isButtonDisabled}
                          className="text-base w-full flex justify-center items-center py-3.5 px-4 bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-lg text-white font-medium transition-all duration-300 animate-float-subtle btn-hover-effect relative overflow-hidden group"
                        >
                          <span className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-red-500/0 via-white/10 to-red-500/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
                          <span className="flex items-center">
                            {isProcessing ? (
                              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : null}
                            <span>{isProcessing ? 'Updating Password...' : 'Reset Password'}</span>
                            {!isProcessing && <ArrowRight className="ml-2" size={18} />}
                          </span>
                        </button>
                      </form>
                    </>
                  ) : (
                    /* Success screen */
                    <div className="text-center py-2">
                      <div className="mx-auto w-16 h-16 bg-gradient-to-r from-red-700 via-red-800 to-red-950 rounded-full flex items-center justify-center animate-glow mb-6">
                        <CheckCircle size={26} className="text-white animate-pulse-soft" />
                      </div>
                      <h2 className="text-2xl font-bold mb-1">Password Updated</h2>
                      <p className="text-pink-300 mb-6">
                        Your password has been successfully changed.
                      </p>
                      <a 
                        href={LoginRoute} 
                        className="inline-flex items-center text-red-400 hover:text-red-300 transition-colors font-medium"
                      >
                        <ArrowLeft size={16} className="mr-1" />
                        Back to login
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
    );
};

export default ResetPasswordComponent;